import { NavLink, Link, useNavigate } from "react-router-dom";
import { House, FileText, PaperPlaneTilt, ChartBar, ChartLine, SignOut, IdentificationCard, Gear, Shield } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import MaintenanceBanner from "@/components/MaintenanceBanner";

// `admin` (praticien) = full access. `assistant` = patients + envois + réponses.
// `research` (assistant de recherche clinique) = réponses (lecture seule) +
// Études pour l'analyse statistique des questionnaires complétés.
const ALL_LINKS = [
  { to: "/dashboard", label: "Accueil", icon: House, roles: ["admin", "assistant", "research"] },
  { to: "/templates", label: "Questionnaires", icon: FileText, roles: ["admin"] },
  { to: "/patient-records", label: "Patients", icon: IdentificationCard, roles: ["admin", "assistant"] },
  { to: "/assignments", label: "Envois", icon: PaperPlaneTilt, roles: ["admin", "assistant"] },
  { to: "/responses", label: "Réponses", icon: ChartBar, roles: ["admin", "assistant", "research"] },
  { to: "/studies", label: "Études", icon: ChartLine, roles: ["admin", "research"] },
  { to: "/settings", label: "Paramètres", icon: Gear, roles: ["admin", "assistant", "research"] },
];

export default function AppShell({ children, title, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  // ``org_admin`` (Direction de l'établissement) reuses the exact same
  // navigation as a solo ``admin`` — the Établissement-specific management
  // is in Settings > Établissement, not a top-level entry. Aliasing here
  // avoids duplicating "admin" in every roles=[…] list below.
  const role = user?.role === "org_admin" ? "admin" : (user?.role || "admin");
  const links = ALL_LINKS.filter((l) => l.roles.includes(role));

  return (
    <div className="min-h-screen bg-[#F4F7FB] flex">
      <aside className="hidden md:flex flex-col w-[260px] bg-[#1E3A5F] text-white px-5 py-6 fixed h-full">
        <Link to="/dashboard" className="flex items-center gap-2.5 mb-10 hover:opacity-90 transition-opacity" data-testid="brand-home-link">
          <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
          <span className="text-[18px] font-bold tracking-tight text-white">Questiomed</span>
        </Link>
        <nav className="space-y-1 flex-1">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/dashboard"}
              data-testid={`nav-${to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-[10px] text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#3A8FA5] text-white"
                    : "text-[#B7C4D6] hover:bg-white/[0.06] hover:text-white"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="pt-4 space-y-2">
          <p className="px-2 text-xs text-[#B7C4D6] leading-relaxed pb-4 border-b border-white/10">
            Besoin d'aide, une suggestion ?{" "}
            <a
              href="mailto:contact@questiomed.fr"
              className="underline underline-offset-2 text-[#7BC3D4] hover:text-white break-words"
              data-testid="contact-mailto"
            >
              Contactez-nous
            </a>
          </p>
          <div className="px-2 py-2 space-y-1">
            <p className="text-sm font-medium text-white break-words leading-snug" data-testid="user-name">
              {user?.title ? `${user.title} ` : ""}{user?.name}
            </p>
            <p className="text-xs text-[#B7C4D6] break-all leading-snug">
              {user?.email}
            </p>
            {role === "assistant" && (
              <span className="inline-block px-1.5 py-0.5 rounded bg-white/10 text-white text-[10px] tracking-wider uppercase">Assistant</span>
            )}
            {role === "research" && (
              <span className="inline-block px-1.5 py-0.5 rounded bg-[#3A8FA5]/30 text-white text-[10px] tracking-wider uppercase">Recherche</span>
            )}
          </div>
          {user?.is_superadmin && !user?._impersonating && (
            <Link
              to="/superadmin"
              className="flex items-center gap-2 mb-1 px-3 py-2 rounded-[10px] bg-[#E4572E]/15 border border-[#E4572E]/30 text-[#FFB59E] hover:bg-[#E4572E]/25 hover:text-white text-sm font-medium transition"
              data-testid="nav-superadmin"
            >
              <Shield size={16} weight="fill" /> Backoffice
            </Link>
          )}
          <Button onClick={doLogout} variant="ghost" size="sm" className="w-full justify-start text-[#B7C4D6] hover:bg-white/[0.06] hover:text-white" data-testid="logout-btn">
            <SignOut size={16} className="mr-2" /> Se déconnecter
          </Button>
        </div>
      </aside>

      <main className="flex-1 md:ml-[260px]">
        <MaintenanceBanner />
        <ImpersonationBanner />
        {user && (user.subscription_status === "trialing" || user.subscription_status === "canceled" || user.subscription_status === "past_due" || user.subscription_status === "active") && (
          (() => {
            const isAdmin = user.role === "admin";
            const status = user.subscription_status;
            const scheduledEnd = user.scheduled_end ? new Date(user.scheduled_end * 1000).toLocaleDateString("fr-FR") : null;
            const suspensionDate = user.suspension_at ? new Date(user.suspension_at * 1000).toLocaleDateString("fr-FR") : null;
            const nextRetry = user.next_payment_attempt ? new Date(user.next_payment_attempt * 1000).toLocaleDateString("fr-FR") : null;
            const cancelScheduled = !!user.cancel_at_period_end || !!user.cancel_at;
            // ── Scheduled cancellation (voluntary) — active OR past_due with
            // a pending cancel. Distinct from a failed payment: no red, just
            // a soft reminder + the option to undo before the effective date.
            // We give this precedence over past_due so a practitioner who
            // just decided to leave doesn't see two contradictory messages.
            if ((status === "active" || status === "past_due") && cancelScheduled && scheduledEnd) {
              return (
                <div className="px-8 py-2 text-sm border-b bg-[#F4F7FB] border-[#3A8FA5]/30 text-[#1E3A5F]" data-testid="trial-banner">
                  <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
                    {isAdmin ? (
                      <span>
                        Résiliation prévue le {scheduledEnd}. Vous pouvez souscrire à nouveau à tout moment avant cette date.
                        {" "}
                        <Link to="/settings?tab=billing" className="underline underline-offset-2 hover:text-[#3A8FA5] font-medium" data-testid="trial-banner-cta">
                          Gérer mon abonnement
                        </Link>
                      </span>
                    ) : (
                      <span data-testid="trial-banner-assistant-scheduled-cancel">
                        Résiliation de l'abonnement du cabinet prévue le {scheduledEnd}. Toutes les fonctionnalités restent accessibles jusqu'à cette date.
                      </span>
                    )}
                  </div>
                </div>
              );
            }
            if (status === "active") return null;
            // ── Cancelled / past_due — differentiate: past_due keeps trying
            // (grace window), canceled is a definitive stop.
            if (status === "canceled" || status === "past_due") {
              const isPastDue = status === "past_due";
              return (
                <div className="px-8 py-2 text-sm border-b bg-[#FBE8E1] border-[#B9502C]/30 text-[#B9502C]" data-testid="trial-banner">
                  <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
                    {isAdmin ? (
                      <span>
                        {isPastDue ? (
                          <>
                            Le prélèvement de votre abonnement a échoué.{" "}
                            {nextRetry && <>Nouvelle tentative le {nextRetry}. </>}
                            {suspensionDate && <>Sans régularisation, votre accès sera suspendu le {suspensionDate}. </>}
                          </>
                        ) : (
                          "Votre abonnement a été résilié. "
                        )}
                        <Link
                          to="/settings?tab=billing"
                          className="underline underline-offset-2 hover:text-[#1E3A5F] font-medium"
                          data-testid="trial-banner-cta"
                        >
                          {isPastDue
                            ? "Mettez à jour votre moyen de paiement pour continuer à utiliser Questiomed."
                            : "Souscrivez un nouvel abonnement pour continuer à utiliser Questiomed."}
                        </Link>
                      </span>
                    ) : user.role === "research" ? (
                      <span data-testid="trial-banner-research-canceled">
                        {isPastDue
                          ? "Le paiement de l'abonnement du cabinet a échoué. L'accès à l'onglet Études est suspendu jusqu'à ce que le praticien titulaire régularise la situation."
                          : "L'abonnement du cabinet a été résilié. L'accès à l'onglet Études est suspendu jusqu'à ce que le praticien titulaire souscrive à nouveau."}
                      </span>
                    ) : (
                      <span data-testid="trial-banner-assistant-canceled">
                        {isPastDue
                          ? "Le paiement de l'abonnement du cabinet a échoué. Les fonctionnalités sont suspendues jusqu'à ce que le praticien titulaire régularise la situation."
                          : "L'abonnement du cabinet a été résilié. Les fonctionnalités sont suspendues jusqu'à ce que le praticien titulaire souscrive à nouveau."}
                      </span>
                    )}
                  </div>
                </div>
              );
            }
            // ── Trialing branch (unchanged) — daysLeft > 0 shows a soft
            // reminder, 0 shows the red expiration message.
            if (!user.trial_ends_at) return null;
            const daysLeft = Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000));
            const expired = daysLeft <= 0;
            return (
              <div className={`px-8 py-2 text-sm border-b ${expired ? "bg-[#FBE8E1] border-[#B9502C]/30 text-[#B9502C]" : "bg-[#EEF3F8] border-[#E1E9F0] text-[#1E3A5F]"}`} data-testid="trial-banner">
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
                  {expired ? (
                    isAdmin ? (
                      <span>
                        Votre période d'essai est terminée.{" "}
                        <Link
                          to="/settings?tab=billing"
                          className="underline underline-offset-2 hover:text-[#1E3A5F] font-medium"
                          data-testid="trial-banner-cta"
                        >
                          Souscrivez un abonnement pour continuer à utiliser Questiomed.
                        </Link>
                      </span>
                    ) : user.role === "research" ? (
                      <span data-testid="trial-banner-research-expired">
                        L'abonnement du cabinet est terminé. L'accès à l'onglet Études est suspendu jusqu'à ce que le praticien titulaire souscrive un abonnement.
                      </span>
                    ) : (
                      <span data-testid="trial-banner-assistant-expired">
                        L'abonnement du cabinet est terminé. Les fonctionnalités sont suspendues jusqu'à ce que le praticien titulaire souscrive un abonnement.
                      </span>
                    )
                  ) : (
                    <span>
                      {isAdmin
                        ? <>Essai gratuit — {daysLeft} jour{daysLeft > 1 ? "s" : ""} restant{daysLeft > 1 ? "s" : ""}. Aucune carte bancaire n'est requise pendant l'essai.{" "}
                            <Link
                              to="/settings?tab=billing"
                              className="underline underline-offset-2 hover:text-[#1E3A5F] font-medium"
                              data-testid="trial-banner-cta"
                            >
                              Passer à l'abonnement
                            </Link>
                          </>
                        : `Cabinet en période d'essai — ${daysLeft} jour${daysLeft > 1 ? "s" : ""} restant${daysLeft > 1 ? "s" : ""}.`}
                    </span>
                  )}
                </div>
              </div>
            );
          })()
        )}
        <div className="px-12 pt-10 pb-12 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
            <h1 className="text-[32px] font-bold tracking-tight text-[#1E3A5F] leading-tight" data-testid="page-title">{title}</h1>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}

export function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB]">
        <div className="flex gap-2">
          <span className="dot w-2 h-2 rounded-full bg-[#3A8FA5]"></span>
          <span className="dot w-2 h-2 rounded-full bg-[#3A8FA5]" style={{ animationDelay: "0.2s" }}></span>
          <span className="dot w-2 h-2 rounded-full bg-[#3A8FA5]" style={{ animationDelay: "0.4s" }}></span>
        </div>
      </div>
    );
  }
  if (!user) {
    nav("/login", { replace: true });
    return null;
  }
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    // ``org_admin`` inherits every admin-only route (no separate frontend
    // gate for it — server enforces org_admin-specific endpoints).
    const currentRole = user.role === "org_admin" ? "admin" : (user.role || "admin");
    if (!allowed.includes(currentRole)) {
      nav("/dashboard", { replace: true });
      return null;
    }
  }
  return children;
}
